import React, { useEffect, useRef, useState } from 'react';
import { RefreshCw, X } from 'lucide-react';

// Cada deploy cambia el nombre del bundle (main.<hash>.js), pero la app de
// escritorio y las pestanas que nunca se cierran siguen con el viejo. Aqui se
// compara con el del servidor: si cambio, ofrece recargar y se recarga sola
// cuando nadie la esta usando. En la app: en la bandeja o minimizada y 30 min
// sin tocarla. En el navegador, 2 h oculta y sin tocarla: Chrome da por oculta
// una pestana tapada por otra ventana (un Excel) y no hay que borrarle a nadie
// algo a medio escribir.
const CADA_MS = 5 * 60 * 1000;
const SIN_USO_APP_MS = 30 * 60 * 1000;
const SIN_USO_NAVEGADOR_MS = 2 * 60 * 60 * 1000;
const ventanaALaVista = typeof window !== 'undefined' ? window.electronAPI?.ventanaALaVista : null;
const MIN_ENTRE_REVISIONES_MS = 60 * 1000;

const bundleCargado = () =>
  document.querySelector('script[src*="/static/js/main."]')?.getAttribute('src') || null;

async function bundleDelServidor() {
  const r = await fetch(`/asset-manifest.json?v=${Date.now()}`, { cache: 'no-store' });
  if (!r.ok) return null;
  const datos = await r.json();
  return datos?.files?.['main.js'] || null;
}

const VersionNueva = () => {
  const [nueva, setNueva] = useState(false);
  const [cerrado, setCerrado] = useState(false);
  const ultimoUso = useRef(Date.now());

  useEffect(() => {
    const cargado = bundleCargado();
    if (process.env.NODE_ENV !== 'production' || !cargado) return undefined;
    let hayNueva = false;
    let ultimaRevision = 0;

    const recargarSiNadieLaUsa = async () => {
      const sinUso = Date.now() - ultimoUso.current;
      if (ventanaALaVista) {
        if (sinUso >= SIN_USO_APP_MS && !(await ventanaALaVista())) window.location.reload();
      } else if (document.visibilityState === 'hidden' && sinUso >= SIN_USO_NAVEGADOR_MS) {
        window.location.reload();
      }
    };
    const revisar = async () => {
      if (hayNueva) {
        recargarSiNadieLaUsa();
        return;
      }
      if (Date.now() - ultimaRevision < MIN_ENTRE_REVISIONES_MS) return;
      ultimaRevision = Date.now();
      try {
        const delServidor = await bundleDelServidor();
        if (delServidor && delServidor !== cargado) {
          hayNueva = true;
          setNueva(true);
          recargarSiNadieLaUsa();
        }
      } catch {
        // Sin conexion: se vuelve a intentar en la proxima vuelta
      }
    };
    const usar = () => {
      ultimoUso.current = Date.now();
    };
    const EVENTOS_USO = ['pointerdown', 'keydown', 'wheel'];

    const id = setInterval(revisar, CADA_MS);
    document.addEventListener('visibilitychange', revisar);
    window.addEventListener('focus', revisar);
    EVENTOS_USO.forEach((ev) => window.addEventListener(ev, usar, { passive: true, capture: true }));
    revisar();
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', revisar);
      window.removeEventListener('focus', revisar);
      EVENTOS_USO.forEach((ev) => window.removeEventListener(ev, usar, { capture: true }));
    };
  }, []);

  if (!nueva || cerrado) return null;

  return (
    <div
      role="status"
      className="fixed bottom-5 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-3 pl-4 pr-2 py-2 rounded-xl bg-slate-900/95 text-white shadow-2xl ring-1 ring-white/10 animate-fadeIn"
    >
      <RefreshCw size={16} className="text-cyan-300 shrink-0" aria-hidden="true" />
      <span className="text-sm whitespace-nowrap">Hay una versión nueva de Centro SURA</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="px-3 py-1.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-900 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
      >
        Recargar
      </button>
      <button
        type="button"
        onClick={() => setCerrado(true)}
        aria-label="Ahora no"
        title="Ahora no"
        className="p-1.5 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-200"
      >
        <X size={16} />
      </button>
    </div>
  );
};

export default VersionNueva;
